import React from 'react';
import { FilterFilled } from '@ant-design/icons';
import { Input } from 'antd';
import { Button, Select, RangePicker } from '@jcm/design-system';
import TruncatedTextTooltip from '../../../../../../shared/components/truncatedTextTooltip/TruncatedTextTooltip';
import { TObrigacao } from '../../../ts/types/ObrigacaoEtapa/Obrigacoes/Obrigacoes';

// Define os tipos para as funções de filtro
type SetFilterText = (prevState: { [key: string]: any }) => { [key: string]: any };

export const criarFiltroTextoDropdown = (
	placeholder: string,
	columnKey: string,
	filterText: { [key: string]: any },
	setFilterText: (callback: SetFilterText) => void,
) => (
	<div style={{ padding: 8 }}>
		<Input
			placeholder={placeholder}
			value={filterText[columnKey]}
			onChange={(e) => setFilterText((prev) => ({ ...prev, [columnKey]: e.target.value }))}
			style={{ marginBottom: 8, display: 'block' }}
		/>
		<Button
			type='filled'
			onClick={() => setFilterText((prev) => ({ ...prev, [columnKey]: '' }))}
			style={{ width: '100%' }}
		>
			Resetar
		</Button>
	</div>
);

export const criarFiltroSelectDropdown = (
	placeholder: string,
	columnKey: string,
	options: string[],
	filterText: { [key: string]: any },
	setFilterText: (callback: SetFilterText) => void,
) => (
	<div style={{ padding: 8 }}>
		<Select
			showSearch
			placeholder={placeholder}
			style={{ width: '100%' }}
			options={options.map((option) => ({ label: option, value: option }))}
			onChange={(value) => setFilterText((prev) => ({ ...prev, [columnKey]: value }))}
			allowClear
		/>
	</div>
);

export const criarFiltroRangePickerDropdown = (
	columnKey: string,
	filterText: { [key: string]: any },
	setFilterText: (callback: SetFilterText) => void,
) => (
	<div style={{ padding: 8 }}>
		<RangePicker
			onChange={(dates) => setFilterText((prev) => ({ ...prev, [columnKey]: dates }))}
			value={filterText[columnKey]}
			format='DD/MM/YYYY'
			style={{ marginBottom: 8, display: 'block', width: '100%' }}
		/>
		<Button
			type='filled'
			onClick={() => setFilterText((prev) => ({ ...prev, [columnKey]: [] }))}
			style={{ width: '100%' }}
		>
			Resetar
		</Button>
	</div>
);

export const colunasTabela = (
	obrigacoes: TObrigacao[],
	filterText: { [key: string]: any },
	setFilterText: (callback: SetFilterText) => void,
	estilo: any,
) => [
	{
		title: 'Código',
		dataIndex: 'codigo',
		key: 'codigo',
		width: '10%',
		sorter: (a: TObrigacao, b: TObrigacao) => Number(a.codigo) - Number(b.codigo),
		filterDropdown: () => criarFiltroTextoDropdown('Buscar ID', 'codigo', filterText, setFilterText),
		filterIcon: (filtered: boolean) => <FilterFilled style={{ color: filtered ? '#1890ff' : undefined }} />,
		onFilter: (value: string | boolean | React.Key, record: TObrigacao) =>
			String(record.codigo).toLowerCase().includes(String(value).toLowerCase()),
		filteredValue: filterText['codigo'] ? [filterText['codigo']] : null,
	},
	{
		title: 'Descrição',
		dataIndex: 'nome',
		key: 'nome',
		width: '30%',
		render: (text: string) => <TruncatedTextTooltip text={text} maxWidth='300px' />,
		sorter: (a: TObrigacao, b: TObrigacao) => a.nome.localeCompare(b.nome),
		filterDropdown: () => criarFiltroTextoDropdown('Buscar descrição', 'nome', filterText, setFilterText),
		filterIcon: (filtered: boolean) => <FilterFilled style={{ color: filtered ? '#1890ff' : undefined }} />,
		onFilter: (value: string | boolean | React.Key, record: TObrigacao) =>
			record.nome ? record.nome.toLowerCase().includes(String(value).toLowerCase()) : false,
		filteredValue: filterText['nome'] ? [filterText['nome']] : null,
	},
	{
		title: 'Responsável',
		dataIndex: 'responsavel',
		key: 'responsavel',
		width: '25%',
		render: (_: unknown, obrigacao: TObrigacao) =>
			obrigacao.responsaveis.find((r) => r.principal)?.funcionario.nome || 'N/A',
		sorter: (a: TObrigacao, b: TObrigacao) => {
			const nomeA = a.responsaveis.find((r) => r.principal)?.funcionario.nome || '';
			const nomeB = b.responsaveis.find((r) => r.principal)?.funcionario.nome || '';
			return nomeA.localeCompare(nomeB);
		},
		filterDropdown: () =>
			criarFiltroSelectDropdown(
				'Selecionar Responsável',
				'responsavel',
				Array.from(new Set(obrigacoes.flatMap((obrigacao) => obrigacao.responsaveis.map((r) => r.funcionario.nome)))),
				filterText,
				setFilterText,
			),
		onFilter: (value: string | boolean | React.Key, record: TObrigacao) => {
			const nomeResponsavel = record.responsaveis.find((r) => r.principal)?.funcionario.nome || '';
			return nomeResponsavel === value;
		},
		filteredValue: filterText['responsavel'] ? [filterText['responsavel']] : null,
	},
	{
		title: 'Aprovador',
		dataIndex: 'aprovador',
		key: 'aprovador',
		width: '25%',
		render: (_: unknown, obrigacao: TObrigacao) =>
			obrigacao.aprovadores.find((a) => a.principal)?.funcionario.nome || 'N/A',
		sorter: (a: TObrigacao, b: TObrigacao) => {
			const nomeA = a.aprovadores.find((a) => a.principal)?.funcionario.nome || '';
			const nomeB = b.aprovadores.find((a) => a.principal)?.funcionario.nome || '';
			return nomeA.localeCompare(nomeB);
		},
		filterDropdown: () =>
			criarFiltroSelectDropdown(
				'Selecionar Aprovador',
				'aprovador',
				Array.from(new Set(obrigacoes.flatMap((obrigacao) => obrigacao.aprovadores.map((a) => a.funcionario.nome)))),
				filterText,
				setFilterText,
			),
		onFilter: (value: string | boolean | React.Key, record: TObrigacao) => {
			const nomeAprovador = record.aprovadores.find((a) => a.principal)?.funcionario.nome || '';
			return nomeAprovador === value;
		},
		filteredValue: filterText['aprovador'] ? [filterText['aprovador']] : null,
	},
	{
		title: 'Vencimento',
		dataIndex: 'dataVencimento',
		key: 'dataVencimento',
		width: '10%',
		render: (text: string) => (
			<div className={estilo.dataVencimentoContainer}>
				<span className={estilo.dataVencimentoText}>
					{new Date(text).toLocaleDateString('pt-BR', {
						day: '2-digit',
						month: '2-digit',
						year: 'numeric',
					})}
				</span>
			</div>
		),
		sorter: (a: TObrigacao, b: TObrigacao) =>
			new Date(a.dataVencimento).getTime() - new Date(b.dataVencimento).getTime(),
		filterDropdown: () => criarFiltroRangePickerDropdown('dataVencimento', filterText, setFilterText),
		filterIcon: (filtered: boolean) => <FilterFilled style={{ color: filtered ? '#1890ff' : undefined }} />,
		onFilter: (_: unknown, record: TObrigacao) => {
			const [start, end] = filterText['dataVencimento'] || [];
			if (!start || !end) return true;
			const recordDate = new Date(record.dataVencimento);
			return recordDate >= start.startOf('day').toDate() && recordDate <= end.endOf('day').toDate();
		},
		filteredValue: filterText['dataVencimento']?.length ? filterText['dataVencimento'] : null,
	},
];
